import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import Svg, { Path, Defs, LinearGradient, Stop, G, Rect } from 'react-native-svg';
import { useTheme } from '../../theme/ThemeContext';

interface WorkIconProps {
  focused: boolean;
  color?: string;
  size?: number;
}

const AnimatedPath = Animated.createAnimatedComponent(Path);
const AnimatedRect = Animated.createAnimatedComponent(Rect);
const AnimatedG = Animated.createAnimatedComponent(G);

const WorkIcon: React.FC<WorkIconProps> = ({ focused, color = '#000000', size = 24 }) => {
  const fillOpacity = useRef(new Animated.Value(focused ? 1 : 0)).current;
  const strokeOpacity = useRef(new Animated.Value(focused ? 0 : 1)).current;
  const scale = useRef(new Animated.Value(1)).current;
  const { isDark } = useTheme();
  
  // Use white in light mode, black in dark mode for the accent strokes
  const accentColor = isDark ? '#1A1A1A' : '#FCEFD2';

  useEffect(() => {
    Animated.parallel([
      Animated.spring(fillOpacity, {
        toValue: focused ? 1 : 0,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
      Animated.spring(strokeOpacity, {
        toValue: focused ? 0 : 1,
        useNativeDriver: false,
        tension: 100,
        friction: 7,
      }),
    ]).start();
  }, [focused]);

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Svg width={size} height={size} viewBox="0 0 48 48" fill="none">
        <Defs>
          <LinearGradient id="workGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <Stop offset="0%" stopColor="#D4A655" stopOpacity="1" />
            <Stop offset="50%" stopColor="#C4963E" stopOpacity="1" />
            <Stop offset="100%" stopColor="#A67D2E" stopOpacity="1" />
          </LinearGradient>
        </Defs>

        {/* Focused state: filled briefcase */}
        <AnimatedG opacity={fillOpacity}>
          <AnimatedRect
            x="6"
            y="12"
            width="36"
            height="26"
            rx="6"
            fill={focused ? 'url(#workGradient)' : color}
          />
          <AnimatedPath
            d="M16 12V9C16 6.791 17.791 5 20 5H28C30.209 5 32 6.791 32 9V12"
            stroke="#C4963E"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <AnimatedRect
            x="12"
            y="22"
            width="24"
            height="7"
            rx="3.5"
            fill={accentColor}
          />
        </AnimatedG>

        {/* Unfocused state: outline briefcase */}
        <AnimatedG opacity={strokeOpacity}>
          <AnimatedRect
            x="6"
            y="12"
            width="36"
            height="26"
            rx="6"
            stroke={color}
            strokeWidth="2.5"
          />
          <AnimatedPath
            d="M16 12V9C16 6.791 17.791 5 20 5H28C30.209 5 32 6.791 32 9V12"
            stroke={color}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <AnimatedRect
            x="12"
            y="22"
            width="24"
            height="7"
            rx="3.5"
            stroke={color}
            strokeWidth="2"
          />
          <AnimatedPath
            d="M6 23H42"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </AnimatedG>
      </Svg>
    </Animated.View>
  );
};

export default WorkIcon;
